import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import Player from '@vimeo/player';
import { HttpClient } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { from } from 'rxjs';
import { switchMap, map, shareReplay } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import type { PianoAnalyticsMedia, PianoAnalyticsData } from '@candyland/generic/shared/model-nmo';

const CONVERT_SECONDS = 1000;

@Injectable({
  providedIn: 'root',
})
export class VimeoService {
  private previousCursorPosition: number;
  private previousPlayerStatePaused = false;
  private playerInit = false;
  private player: Player;
  private pa_player: PianoAnalyticsMedia;
  private pa_data: PianoAnalyticsData;

  constructor(private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object) {}

  public fetchVimeoData$(videoId: string): Observable<VimeoResponse> {
    return this.http.get<VimeoResponse>(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`).pipe(
      map((response: VimeoResponse) => response),
      shareReplay(1)
    );
  }

  public initVimeo(iFrame: HTMLElement, videoId: string): void {
    this.player = new Player(iFrame, {
      id: videoId,
    });

    let eventAttached = false;
    this.player.ready().then(() => {
      from(this.player.getVideoUrl())
        .pipe(
          switchMap((videoUrl: string) =>
            this.fetchVimeoData$(videoId).pipe(
              map((response: VimeoResponse) => {
                this.pa_data = {
                  av_content_id: parseInt(videoId, 10),
                  av_content: response.title,
                  av_content_type: 'Video',
                  av_content_duration: response.duration,
                  av_player: 'Vimeo',
                  av_publication_date: response.upload_date,
                  av_author: response.author_name,
                  av_url: videoUrl,
                };

                return this.pa_data;
              })
            )
          )
        )
        .subscribe((pa_data: PianoAnalyticsData) => {
          if (eventAttached || !window.pa) {
            return;
          }

          this.player.on('playing', (data: TimeEvent) => {
            if (this.previousPlayerStatePaused === false) {
              this.trackEvent(pa_data, 0, 'bufferHeartbeat');
              this.trackEvent(pa_data, 0, 'play');
              this.trackEvent(pa_data, 0, 'playbackStart');
            } else {
              this.trackEvent(pa_data, data.seconds * CONVERT_SECONDS, 'rebufferHeartbeat');
              this.trackEvent(pa_data, data.seconds * CONVERT_SECONDS, 'playbackResumed');
            }
          });

          this.player.on('pause', (data: TimeEvent) => {
            if (data.seconds !== data.duration) {
              this.trackEvent(pa_data, data.seconds * CONVERT_SECONDS, 'playbackPaused');
              this.previousPlayerStatePaused = true;
            }
          });

          this.player.on('ended', (data: TimeEvent): void => {
            this.trackEvent(pa_data, data.seconds * CONVERT_SECONDS, 'playbackStopped');
          });

          this.player.on('bufferstart', (data: TimeEvent): void => {
            this.trackEvent(pa_data, data.seconds * CONVERT_SECONDS, 'bufferStart');
          });

          this.player.on('seeking', (data: TimeEvent): void => {
            this.previousCursorPosition = data.seconds * CONVERT_SECONDS;
          });

          this.player.on('seeked', (data: TimeEvent): void => {
            this.trackEvent(pa_data, data.seconds * CONVERT_SECONDS, 'seek', this.previousCursorPosition);
          });

          eventAttached = true;
        });
    });
  }

  // To avoid continued heartbeat events after changing the page, kindly transmit a pause event
  public pauseTracking(): void {
    this.trackEvent(this.pa_data, 0, 'playbackPaused');
  }

  private initPaPlayer(): void {
    if (isPlatformBrowser(this.platformId) && window.pa) {
      this.pa_player = new window.pa.avInsights.Media({ 0: 5, 1: 10, 5: 60 }, 5);
      this.playerInit = true;
    }
  }

  private trackEvent(
    data: PianoAnalyticsData,
    seconds: number,
    playbackMethod: string,
    previousSeconds?: number
  ): void {
    if (!this.playerInit) {
      this.initPaPlayer();
    }

    // in case the init did not work. this crashed the liveblog before SD-14565
    if (this.playerInit) {
      this.pa_player.setProps(data);
      if (previousSeconds !== undefined && playbackMethod === 'seek') {
        this.pa_player[playbackMethod](previousSeconds, seconds);
      } else {
        this.pa_player[playbackMethod](seconds);
      }
    }
  }
}

declare global {
  interface Window {
    pa: {
      avInsights: {
        Media(heartbeatDuration?: number | object, bufferingHeartbeatDuration?: number, sessionId?: string): void;
        setProps(properties: object): void;
      };
      sendEvent: (event: string, data: Object) => void;
    };
  }
}

export interface TimeEvent {
  duration: number;
  percent: number;
  seconds: number;
}

export interface VimeoResponse {
  type: string;
  version: string;
  provider_name: string;
  provider_url: string;
  title: string;
  author_name: string;
  author_url: string;
  is_plus: string;
  account_type: string;
  html: string;
  width: number;
  height: number;
  duration: number;
  description: string;
  thumbnail_url: string;
  thumbnail_width: number;
  thumbnail_height: number;
  thumbnail_url_with_play_button: string;
  upload_date: string;
  video_id: number;
  uri: string;
}
